/**
 * Back-navigation for the project detail screen.
 *
 * The board hands the detail screen the exact URL it was showing (filters included)
 * as `location.state.returnTo`. Back then returns to that list rather than resetting
 * to the unfiltered dashboard, so Editor -> Project -> Back lands on the editor's
 * project list instead of the main board.
 */

/**
 * Accept a return path only if it is an in-app absolute path. `//evil.com` is a
 * protocol-relative URL that the browser would treat as another origin, so it is
 * rejected along with anything that is not a string starting with a single slash.
 */
export const resolveReturnTo = (rawReturnTo) =>
  typeof rawReturnTo === 'string' && rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//')
    ? rawReturnTo
    : null;

/** The query string of a return path, as URLSearchParams. */
const paramsOf = (returnTo) => new URLSearchParams((returnTo || '').split('?')[1] || '');

/** The path of a return path, without its query string. */
const pathOf = (returnTo) => (returnTo || '').split('?')[0];

/** The editor the board was filtered to, or null. */
export const editorIdFromReturnTo = (returnTo) => (returnTo ? paramsOf(returnTo).get('editor') : null);

/** Whether the board was filtered to unassigned projects. */
export const isUnassignedReturnTo = (returnTo) =>
  returnTo ? paramsOf(returnTo).get('unassigned') === 'true' : false;

/**
 * Label the Back control for wherever it actually goes. Naming the editor is the
 * point of the feature — it tells the user the list they came from is still there.
 */
export const backButtonLabel = ({ returnTo, members = [], isFromTeamsDashboard = false } = {}) => {
  // The hidden-projects queue is its own list, not a filtered board, so it is named
  // by its path rather than by a query parameter.
  if (pathOf(returnTo) === '/hiddenprojects') return 'Back to Hidden Projects';

  const editorId = editorIdFromReturnTo(returnTo);
  if (editorId) {
    const name = (members || []).find((m) => String(m?.id) === String(editorId))?.name;
    return name ? `Back to ${name}'s Projects` : "Back to Editor's Projects";
  }
  if (isUnassignedReturnTo(returnTo)) return 'Back to Unassigned Projects';
  return isFromTeamsDashboard ? 'Back to Team Dashboard' : 'Back to Main Dashboard';
};
